/**
 * Get base server URL (tanpa /api)
 */
export const getServerUrl = (): string => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    return apiUrl.replace(/\/api\/?$/, '');
};

/**
 * Normalize storage path to full URL
 * Handles various path formats from backend
 * 
 * @param path - Path dari backend (bisa relatif atau absolute)
 * @param cacheBuster - Optional timestamp untuk cache busting
 */
export const getStorageUrl = (
    path: string | null | undefined,
    cacheBuster?: string | number
): string | undefined => {
    if (!path) return undefined;

    // Sudah full URL
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return cacheBuster ? `${path}?v=${cacheBuster}` : path;
    }

    const serverUrl = getServerUrl();

    // Clean redundant and double slashes
    let normalizedPath = path.replace(/\/+/g, '/');

    // Ensure it starts with /storage/
    if (normalizedPath.startsWith('/storage/')) {
        // already has prefix
    } else if (normalizedPath.startsWith('storage/')) {
        normalizedPath = `/${normalizedPath}`;
    } else if (normalizedPath.startsWith('/')) {
        normalizedPath = `/storage${normalizedPath}`;
    } else {
        normalizedPath = `/storage/${normalizedPath}`;
    }

    // Encode path segments (handle special characters in filename)
    const encodedPath = normalizedPath
        .split('/')
        .map((segment, index) => {
            // Skip empty segment (first one from leading slash)
            if (index === 0 && segment === '') return '';
            // Don't encode 'storage' keyword
            if (segment === 'storage') return segment;
            return encodeURIComponent(segment);
        })
        .join('/');

    const fullUrl = `${serverUrl}${encodedPath}`;

    return cacheBuster ? `${fullUrl}?v=${cacheBuster}` : fullUrl;
};

/**
 * Get avatar URL with cache busting
 */
export const getAvatarUrl = (
    avatarPath: string | null | undefined,
    updatedAt?: string | null
): string | undefined => {
    if (!avatarPath) return undefined;

    let normalizedAvatarPath = avatarPath;

    if (avatarPath.startsWith('http://') || avatarPath.startsWith('https://')) {
        try {
            normalizedAvatarPath = new URL(avatarPath).pathname;
        } catch {
            normalizedAvatarPath = avatarPath;
        }
    }

    // Use updated_at timestamp for cache busting, or current timestamp
    const parsedTimestamp = updatedAt && /^\d+$/.test(updatedAt)
        ? Number(updatedAt)
        : NaN;

    const parsedDateTimestamp = updatedAt ? new Date(updatedAt).getTime() : NaN;

    const cacheBuster = Number.isFinite(parsedTimestamp)
        ? parsedTimestamp
        : Number.isFinite(parsedDateTimestamp)
            ? parsedDateTimestamp
            : Date.now();

    const avatarMatch = normalizedAvatarPath.match(/(?:^|\/)storage\/avatars\/([^/?#]+)$/);
    if (avatarMatch?.[1]) {
        const publicAvatarUrl = `${getServerUrl()}/api/public/avatars/${encodeURIComponent(avatarMatch[1])}`;
        return `${publicAvatarUrl}?v=${cacheBuster}`;
    }

    return getStorageUrl(avatarPath, cacheBuster);
};

/**
 * Get asset URL (logo, favicon, etc) - no cache busting by default
 */
export const getAssetUrl = (
    assetPath: string | null | undefined
): string | undefined => {
    if (!assetPath) return undefined;

    let normalizedAssetPath = assetPath;

    if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
        try {
            normalizedAssetPath = new URL(assetPath).pathname;
        } catch {
            return assetPath;
        }
    }



    return getStorageUrl(assetPath);
};
